import React, { useState, useMemo } from 'react';
import './DailyTrendChartBMI.css';

const DailyTrendChartBMI = ({ measurements }) => {
    // Calculate daily averages for chart
    const chartData = useMemo(() => {
        if (!measurements || measurements.length === 0) return { dates: [], bmiData: [], measurementDetails: {} };

        // Group measurements by date and store details for tooltips
        const dailyData = {};

        measurements.forEach(m => {
            const date = m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0];
            const time = m.measurement_time ||
                m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                m.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                m.created_at?.split('T')[1]?.slice(0, 5) ||
                '';

            if (!dailyData[date]) {
                dailyData[date] = { bmi: [], measurements: [] };
            }

            if (m.bmi != null) {
                const val = parseFloat(m.bmi);
                dailyData[date].bmi.push(val);
                dailyData[date].measurements.push({
                    date,
                    time,
                    bmi: val,
                });
            }
        });

        // Calculate averages and sort by date
        const sortedDates = Object.keys(dailyData).sort((a, b) => new Date(a) - new Date(b));
        const bmiData = sortedDates.map(date => {
            const values = dailyData[date].bmi;
            return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
        });

        return { dates: sortedDates, bmiData, measurementDetails: dailyData };
    }, [measurements]);

    // Determine dynamic Y-axis min/max
    const yDomain = useMemo(() => {
        const validBMIs = chartData.bmiData.filter(v => v != null);
        if (validBMIs.length === 0) return { min: 15, max: 35, step: 5 };

        let rawMin = Math.min(...validBMIs);
        let rawMax = Math.max(...validBMIs);

        if (rawMin === rawMax) {
            rawMin -= 1;
            rawMax += 1;
        }

        const padding = (rawMax - rawMin) * 0.2;
        const min = Math.max(0, Math.floor(rawMin - padding));
        let max = Math.ceil(rawMax + padding);

        const range = max - min;
        let step = range > 10 ? Math.ceil(range / 5) : (range > 5 ? 2 : (range <= 2 ? 0.25 : 1));
        max = min + (step * 5);

        return { min, max, step };
    }, [chartData]);


    // Chart dimensions and scaling
    const chartWidth = 800;
    const chartHeight = 250;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    const scaleX = (index) => {
        if (chartData.dates.length <= 1) return padding.left + (plotWidth / 2);
        return padding.left + (index / (chartData.dates.length - 1)) * plotWidth;
    };

    const scaleY = (value) => {
        if (value == null) return null;
        return padding.top + plotHeight - ((value - yDomain.min) / (yDomain.max - yDomain.min)) * plotHeight;
    };

    // Generate smooth SVG path for line (Catmull-Rom spline approximation or simple bezier)
    const generateSmoothPath = (data) => {
        const points = data
            .map((value, index) => {
                const y = scaleY(value);
                if (y == null) return null;
                return { x: scaleX(index), y };
            })
            .filter(p => p != null);

        if (points.length === 0) return '';
        if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

        let path = `M ${points[0].x} ${points[0].y}`;
        for (let i = 0; i < points.length - 1; i++) {
            const p0 = points[i === 0 ? 0 : i - 1];
            const p1 = points[i];
            const p2 = points[i + 1];
            const p3 = points[i + 2 === points.length ? i + 1 : i + 2];

            // Use a simple cubic bezier based on tension
            const tension = 0.2;
            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }
        return path;
    };

    const formatChartDate = (dateStr) => {
        const date = new Date(dateStr);
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    };

    const yTicks = Array.from({ length: 6 }, (_, i) => yDomain.min + (yDomain.step * i)).reverse();
    const [tooltip, setTooltip] = useState(null);

    return (
        <div className="dtcb-chart-section">
            <h4 className="dtcb-title">Daily Trend Chart of BMI</h4>

            <div className="dtcb-chart-container">
                <div className="dtcb-chart-title-center">Daily trend chart of bmi</div>
                <div className="dtcb-chart-legend">
                    <div className="dtcb-legend-item">
                        <div className="dtcb-legend-line dtcb-legend-bmi"></div>
                        <span>weight</span> {/* Exact label from image */}
                    </div>
                </div>

                <div className="dtcb-chart-area">
                    {chartData.dates.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
                    ) : (
                        <div className="dtcb-chart-placeholder">
                            <div className="dtcb-y-axis">
                                {yTicks.map(tick => (
                                    <span key={tick}>{tick.toFixed(2)}</span>
                                ))}
                            </div>
                            <div className="dtcb-chart-grid" style={{ width: '100%', height: '100%' }}>
                                <svg
                                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                                    preserveAspectRatio="none"
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                >
                                    {yTicks.map(value => {
                                        const y = scaleY(value);
                                        if (y == null) return null;
                                        return (
                                            <line
                                                key={`grid-${value}`}
                                                x1={padding.left}
                                                y1={y}
                                                x2={chartWidth - padding.right}
                                                y2={y}
                                                stroke="#ECEDEE"
                                                strokeWidth="1"
                                            />
                                        );
                                    })}

                                    {chartData.bmiData.some(v => v != null) && (
                                        <path
                                            d={generateSmoothPath(chartData.bmiData)}
                                            fill="none"
                                            stroke="#4B8B9B"
                                            strokeWidth="2"
                                        />
                                    )}

                                    {chartData.dates.map((date, index) => {
                                        const bmiValue = chartData.bmiData[index];
                                        const x = scaleX(index);
                                        if (bmiValue == null) return null;

                                        const handleMouseEnter = (e) => {
                                            const rect = e.currentTarget.closest('.dtcb-chart-grid')?.getBoundingClientRect();
                                            if (!rect) return;
                                            setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, date, bmiValue });
                                        };
                                        const handleMouseLeave = () => setTooltip(null);

                                        return (
                                            <g key={`points-${date}`}>
                                                <line
                                                    x1={x}
                                                    y1={padding.top}
                                                    x2={x}
                                                    y2={chartHeight - padding.bottom}
                                                    stroke="transparent"
                                                    strokeWidth="20"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={handleMouseEnter}
                                                    onMouseLeave={handleMouseLeave}
                                                />
                                            </g>
                                        );
                                    })}
                                </svg>

                                <div className="dtcb-x-axis">
                                    {chartData.dates.map((date, index) => {
                                        const skip = Math.ceil(chartData.dates.length / 5) || 1;
                                        if (index % skip === 0 || index === chartData.dates.length - 1) {
                                            return <span key={date} style={{ left: `${(scaleX(index) / chartWidth) * 100}%` }}>{formatChartDate(date)}</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                {tooltip && (
                                    <div
                                        className="dtcb-tooltip"
                                        style={{ left: `${tooltip.x}px`, top: `${tooltip.y - 40}px`, transform: 'translateX(-50%)' }}
                                    >
                                        <div className="dtcb-tooltip-content">
                                            <div className="dtcb-tooltip-date">{formatChartDate(tooltip.date)}</div>
                                            <div className="dtcb-tooltip-item">
                                                <span className="dtcb-tooltip-value">{tooltip.bmiValue.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DailyTrendChartBMI;
